export const S3_CHUNK_SIZE = 5 * 1024 * 1024;
export const S3_MULTIPART_THRESHOLD_BYTES = 20 * 1024 * 1024;

export type MultipartUploadPart = {
  ETag: string;
  PartNumber: number;
};

type UploadMultipartFileOptions = {
  file: File;
  uploadId: string;
  filepath: string;
  getPartUrl: (partNumber: number) => Promise<string | undefined>;
  complete: (parts: MultipartUploadPart[]) => Promise<void>;
  onProgress?: (progress: number) => void;
  uploadErrorMessage: string;
  uploadLinkErrorMessage: string;
};

export const uploadMultipartFileToS3 = async ({
  file,
  uploadId,
  filepath,
  getPartUrl,
  complete,
  onProgress,
  uploadErrorMessage,
  uploadLinkErrorMessage,
}: UploadMultipartFileOptions) => {
  const totalParts = Math.ceil(file.size / S3_CHUNK_SIZE);
  const parts: MultipartUploadPart[] = [];

  for (let partNumber = 1; partNumber <= totalParts; partNumber += 1) {
    const start = (partNumber - 1) * S3_CHUNK_SIZE;
    const end = Math.min(start + S3_CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);
    const url = await getPartUrl(partNumber);

    if (!url) {
      throw new Error(uploadLinkErrorMessage);
    }

    const uploadRes = await fetch(url, {
      method: "PUT",
      body: chunk,
    });

    if (!uploadRes.ok) {
      throw new Error(uploadErrorMessage);
    }

    const etag = uploadRes.headers.get("ETag");
    if (!etag) {
      throw new Error(uploadErrorMessage);
    }

    parts.push({ ETag: etag, PartNumber: partNumber });

    onProgress?.(Math.round((partNumber / totalParts) * 100));
  }

  await complete(parts);

  return {
    uploadId,
    filepath,
    parts,
  };
};
